import type { AbstractLeadFormProps, AbstractLeadFormState } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import { AbstractLeadForm } from "@component/assets/AbstractLeadForm/AbstractLeadForm";
import type { AbstractLeadFormFields, LeadRequest, ProductType } from "@component/assets/AbstractLeadForm/types";
import { formatText } from "@ui/format";
import { JsonDataBuilder } from "@utils/JsonDataBuilder";
import { logError } from "@utils/sentry-error";
import type { ReactNode } from "react";
import React from "react";
import * as Step from "./Steps";
import { calculate } from "./domain";
import type {
	BabyLoanLeadCalcRequest,
	BabyLoanLeadCalcResponse,
	BabyLoanRequest,
	BabyLoanResponse,
	Variant,
} from "./types";
import { ErrorCode } from "./types";
import "./style.scss";

export interface FormFields extends AbstractLeadFormFields, BabyLoanRequest {}

interface Props extends AbstractLeadFormProps<BabyLoanLeadCalcRequest, BabyLoanLeadCalcResponse> {
	variant?: Variant;
}

interface State extends AbstractLeadFormState<FormFields> {
	response: BabyLoanResponse;
	isEventSent?: boolean;
}

/**
 * @Shortcode
 *
 * @title "Babaváró hitel kalkulátor"
 * @shortcode "baby_loan_small"
 * @slug "babavaro-hitel"
 * @type "calculator"
 */
export class BabyLoanSmallCalculator extends AbstractLeadForm<
	BabyLoanLeadCalcRequest,
	BabyLoanLeadCalcResponse,
	FormFields,
	Props,
	State
> {
	// EventParams
	protected readonly productTypes: string = "BABY_LOAN";
	protected readonly componentType: string = "small-calculator";

	public static defaultProps = {
		title: "Add meg az elérhetőséged, és elküldjük a számodra elérhető Babaváró hitel részleteit e-mailben",
		outsideTitle: "Babaváró hitel kalkulátor",
		outsideTitleLink: "/babavaro-hitel/",
		bottomLogo: true,
	};

	protected getInitialState(): State {
		return {
			...super.getInitialState(),
			activeStep: "step-1",
			response: undefined,
		};
	}

	protected renderSteps(): ReactNode {
		const { variant, title } = this.props;
		const {
			submitState,
			leadId,
			values: { email },
		} = this.state;

		return (
			<>
				<Step.Step1 stepName="step-1" defaultTarget="step-2" variant={variant} />
				<Step.Step2 stepName="step-2" defaultTarget="step-3" canStepBack />
				<Step.Step3 stepName="step-3" defaultTarget="step-4" canStepBack />
				<Step.Step4 stepName="step-4" defaultTarget="step-5" canStepBack />
				<Step.Step5 stepName="step-5" defaultTarget={variant === "erste" ? "step-6" : "step-5a"} canStepBack />
				<Step.Step5a stepName="step-5a" defaultTarget="step-6" canStepBack />
				<Step.Step6 stepName="step-6" defaultTarget="lead-form" canStepBack />
				<Step.LeadForm
					stepName="lead-form"
					defaultTarget="success-lead"
					title={title}
					button="Eredmény elküldése"
					variant={variant}
					submitState={submitState}
					canStepBack
					crossSellOption
					newsletterOption
				/>
				<Step.SuccessLead stepName="success-lead" leadId={leadId} email={email} variant={variant} />
				<Step.KoLead stepName="ko-lead" />
			</>
		);
	}

	protected onStep(target: string, withEvent: boolean): void {
		const { values, isEventSent = true } = this.state;

		switch (target) {
			case "step-6":
				this.setState(
					{
						isEventSent: !isEventSent,
					},
					() => {
						if (isEventSent) {
							this.sendCalculationEvent();
						}
					},
				);
				super.onStep(target, withEvent);
				break;

			case "success-lead":
				this.setState(
					{
						submitState: "loading",
						response: calculate(values),
					},
					() => {
						this.sendLead()
							.then(() => {
								const { response } = this.state;

								super.onStep(response?.errors?.length ? "ko-lead" : "success-lead", false);
							})
							.catch((error: Error) => {
								// TODO: Hibakezelés
								logError(error);
							});
					},
				);
				break;
			default:
				super.onStep(target, withEvent);
		}
	}

	/**
	 * Leadbeküldéshez szükséges adatok
	 *
	 * @return Összegűjtött adatok
	 */
	public getLeadData(): LeadRequest<BabyLoanLeadCalcRequest, BabyLoanLeadCalcResponse> {
		const {
			values: { inKHR },
		} = this.state;

		const data = super.getLeadData();
		const {
			response: {
				realInstallment,
				maxInstallment,
				realLoanAmount,
				maxLoanAmount,
				maxFreelyUsableInstallment,
				freelyUsableRealLoanAmount,
				maxFreelyUsableLoanAmount,
				hasAcceptableIncome,
				productName,
				freelyUsableRealInstallment,
			},
		} = data;

		const otherData = new JsonDataBuilder(data.request.otherDataJson);
		otherData.addItem(
			"Mikor szeretnéd igényelni a Babaváró hitelt?",
			"productRequisitionTime",
			data.request.productRequisitionTime,
		);
		otherData.addItem("Úgy szeretnék hitelt felvenni, hogy KHR (BAR) listán vagyok?", "inKHR", inKHR, "bool");
		otherData.addItem("Havi törlesztő KIVÁLTÁS", "realInstallment", realInstallment, "ft");
		otherData.addItem("Max. vállalható törlesztő KIVÁLTÁS", "maxInstallment", maxInstallment, "ft");
		otherData.addItem("Hitelösszeg KIVÁLTÁS", "realLoanAmount", realLoanAmount, "ft");
		otherData.addItem("Max. hitelösszeg KIVÁLTÁS ", "maxLoanAmount", maxLoanAmount, "ft");

		otherData.addItem("Havi törlesztő SZABFEL", "freelyUsableRealInstallment", freelyUsableRealInstallment, "ft");
		otherData.addItem(
			"Max. vállalható törlesztő SZABFEL",
			"maxFreelyUsableInstallment",
			maxFreelyUsableInstallment,
			"ft",
		);
		otherData.addItem("Hitelösszeg SZABFEL", "freelyUsableRealLoanAmount", freelyUsableRealLoanAmount, "ft");
		otherData.addItem("Max. hitelösszeg SZABFEL", "maxFreelyUsableLoanAmount", maxFreelyUsableLoanAmount, "ft");

		otherData.addItem("Van egy jövedelemből minimálbére?", "hasAcceptableIncome", hasAcceptableIncome, "bool");

		data.request = {
			...data.request,
			otherDataJson: otherData.getJson(),
		};
		data.wantsWithOnlineService = productName === "Erste Online Babaváró";

		return data;
	}

	protected getCalculatorRequest(): BabyLoanLeadCalcRequest {
		const {
			values: { extendBabyLoanWith: _1, productName: _2, ...otherRequestData },
		} = this.state;
		otherRequestData.lastName = undefined;
		otherRequestData.firstName = undefined;
		otherRequestData.email = undefined;
		otherRequestData.phone = undefined;
		otherRequestData.cityName = undefined;

		return {
			...super.getCalculatorRequest(),
			...otherRequestData,
		};
	}

	protected getCalculatorResponse(): BabyLoanLeadCalcResponse {
		const {
			values: { extendBabyLoanWith = [], productName },
			response = { errors: [] },
		} = this.state;
		const { errors, ...otherResponseData } = response;

		return {
			...super.getCalculatorResponse(),
			...otherResponseData,
			extendBabyLoanWithCSOK: extendBabyLoanWith.includes("CSOK"),
			extendBabyLoanWithMortgage: extendBabyLoanWith.includes("MORTGAGE"),
			extendBabyLoanWithPersonalLoan: extendBabyLoanWith.includes("PERSONAL_LOAN"),
			ko0: errors.includes(ErrorCode.AGE),
			ko1: errors.includes(ErrorCode.MARRIED),
			ko2: errors.includes(ErrorCode.CHILD),
			ko3: errors.includes(ErrorCode.ADDRESS),
			ko4: errors.includes(ErrorCode.NAV),
			ko5: errors.includes(ErrorCode.KHR),
			ko6: errors.includes(ErrorCode.SOC),
			ko7: errors.includes(ErrorCode.NO_INCOME),
			ko8: errors.includes(ErrorCode.DAYSSOCINHUN),
			hasKo: errors.length !== 0,
			productName: productName || "Erste Online Babaváró",
		};
	}

	protected getAutoresponderName(): string {
		return "autoresponder-baby-loan";
	}

	protected getAutoresponderFields(): Record<string, any> {
		const data = this.getLeadData();

		return {
			lastName: data.lastName,
			firstName: data.firstName,

			isErsteOnline: data.response.productName === "Erste Online Babaváró",

			hasKo: data.response.hasKo,
			ko1: data.response.ko0,
			ko2: data.response.ko1,
			ko3: data.response.ko2,
			ko4: data.response.ko3,
			ko5: data.response.ko4,
			ko6: data.response.ko5,
			ko7: data.response.ko6,
			ko8: data.response.ko7,
			ko9: data.response.ko8,
			creditLimitSumValue: data.request.creditLimitSumValue,
			currentLoansMonthlyInstallment: data.request.currentLoansMonthlyInstallment,
			eligibleForBabyLoanValue: data.response.eligibleForBabyLoanValue,
			realLoanAmount: data.response.realLoanAmount ? formatText(data.response.realLoanAmount, "ft") : undefined,
			realInstallment: data.response.realInstallment
				? formatText(data.response.realInstallment, "ft")
				: undefined,
			freelyUsableRealLoanAmount: formatText(data.response.freelyUsableRealLoanAmount, "ft"),
			freelyUsableRealInstallment: formatText(data.response.freelyUsableRealInstallment, "ft"),
		};
	}

	protected getProductType(): ProductType {
		return "BABY_LOAN";
	}

	protected getClassName(): string {
		return `${super.getClassName()} baby-loan-small`;
	}

	protected getComponentName(): string {
		return "BabyLoanSmallCalculator";
	}
}

export default BabyLoanSmallCalculator;
